<script setup lang="ts">
import { computed } from 'vue'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { WidgetDensity } from '@/composables/useCustomDashboardWidget'
import { useStatusPanelWidget } from '@/composables/widgets/useStatusPanelWidget'
import { useI18n } from '@/composable/useI18n'

const props = withDefaults(
  defineProps<{
    widget: IWidget
    density?: WidgetDensity
  }>(),
  {
    density: 'comfortable',
  },
)

const { t } = useI18n()
const { getDisplay, isLoading, hasError } = useStatusPanelWidget()

const outerClass = computed(() => {
  if (props.density === 'dense') return 'flex min-h-0 flex-1 flex-col px-2 pb-2 pt-1'
  if (props.density === 'compact') return 'flex min-h-0 flex-1 flex-col px-3 pb-3 pt-1.5'
  return 'flex min-h-0 flex-1 flex-col px-4 pb-4 pt-2'
})

const badgeClass = computed(() => {
  const base = 'flex flex-col justify-center'
  if (props.density === 'dense') return `${base} px-2 py-2`
  if (props.density === 'compact') return `${base} px-3 py-4`
  return `${base} px-5 py-6`
})

const statusLabelClass = computed(() => {
  if (props.density === 'dense') return 'font-satoshi text-base font-bold uppercase tracking-wide text-white'
  if (props.density === 'compact') return 'mt-1 font-satoshi text-xl font-bold uppercase tracking-wide text-white'
  return 'mt-2 font-satoshi text-2xl font-bold uppercase tracking-wide text-white'
})

const showStatusHeader = computed(() => props.density !== 'dense')
</script>

<template>
  <div :class="outerClass">
    <div
      v-if="isLoading(widget)"
      class="flex flex-1 items-center justify-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.status_panel.loading') }}
    </div>

    <div
      v-else-if="hasError(widget)"
      class="flex flex-1 items-center justify-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.status_panel.error') }}
    </div>

    <div
      v-else
      class="flex flex-1 flex-col justify-center"
    >
      <div
        :class="[badgeClass, getDisplay(widget).bgClass]"
      >
        <p
          v-if="showStatusHeader"
          class="text-2xs font-medium uppercase tracking-widest text-white/70"
        >
          {{ t('custom_dashboards.view.status_panel.status_label') }}
        </p>
        <p :class="statusLabelClass">
          {{ getDisplay(widget).label }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-satoshi {
  font-family: 'Satoshi', sans-serif;
}
</style>
